import React from "react";
import { useParams, useLocation } from "react-router-dom";
import { Loader } from "../../lib/ui/components";
import { usePurchaseOption } from "../../lib/hooks/usePurchaseOption";
import "./purchase-option.style.scss";
import armanCncLogo from "../../assets/images/purchase-option/armancnc-logo.png";
export const PurchaseOptionComponent = () => {
  const { purchaseOptionId } = useParams<{ purchaseOptionId: string }>();
  const { data, isLoading, error } = usePurchaseOption(purchaseOptionId);

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const status = searchParams.get("status");
  const euroRate = searchParams.get("euro");

  const formatNumber = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === "") return "0";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return isNaN(num) ? "0" : num.toLocaleString("en-US");
  };

  if (isLoading) return <Loader visible />;
  if (error) return <div className="error-message">Error loading data.</div>;
  if (!data) return <div className="error-message">No data found.</div>;

  return (
    <div id="purchase-option-wrapper" dir="rtl">
      <header className="page-header">
        <div className="left-side">
          <span className="custom-id">
            شناسه: {data.purchaseOptionCustomId}
          </span>
        </div>
        <div className="right-side">
          {status && <span className="status-badge">{status}</span>}
          <h1 className="header-title">{data.title}</h1>
          <img
            src={armanCncLogo}
            alt="Arman CNC Logo"
            className="header-logo"
          />
        </div>
      </header>

      <div className="purchase-option-page-container purchase-option-printable-area">
        <table className="data-table">
          <thead>
            <tr>
              <th>شناسه پیشنهاد</th>
              <th>نام محصول</th>
              <th>قیمت خرید</th>
              <th>قیمت پیشنهادی فروش</th>
              <th>کمیسیون سایت حراجی</th>
              <th>هزینه دمونتاژ</th>
              <th>هزینه ارسال به انبار خارجی</th>
              <th>وزن</th>
              <th>هزینه نهایی</th>
            </tr>
          </thead>
          <tbody>
            {data.products.map((product) => (
              <tr key={product.productId}>
                <td>{product.productId}</td>
                <td>{product.productTitle}</td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.productPrice)} €
                  </span>
                </td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.purchasingPriceSuggestion)}
                  </span>
                  <span> میلیون تومان</span>
                </td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.productCommission)} €
                  </span>
                </td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.productDismantlingCost)} €
                  </span>
                </td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.productShippingCostWarehouse)} €
                  </span>
                </td>
                <td>
                  <span className="ltr-value">
                    Kg {formatNumber(product.productWeight)}
                  </span>
                </td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.finalCost)}
                  </span>
                  <span> میلیون تومان</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <h2 className="section-title">گزارش هزینه نهایی محصولات</h2>

        <div className="summary-cards">
          <div className="summary-card">
            <h3>هزینه حمل</h3>
            <p className="ltr-value">{formatNumber(data.shippingCost)} €</p>
          </div>
          <div className="summary-card">
            <h3>کمیسیون واسط</h3>
            <p className="ltr-value">
              {formatNumber(data.resellerCommission)} €
            </p>
          </div>
          <div className="summary-card">
            <h3>هزینه گمرک</h3>
            <p>{formatNumber(data.customsFee)} تومان</p>
          </div>
          <div className="summary-card">
            <h3>هزینه ترخیص</h3>
            <p>{formatNumber(data.clearanceFee)} تومان</p>
          </div>
          <div className="summary-card total-card">
            <h3>جمع کل</h3>
            <p className="ltr-value">{formatNumber(data.totalCost)} €</p>
          </div>
        </div>

        <h2 className="section-title">نتیجه محاسبه سود و زیان</h2>

        {euroRate && (
          <div className="euro-rate-pill">
            نرخ روز یورو: {formatNumber(euroRate)} تومان
          </div>
        )}

        <table className="data-table">
          <thead>
            <tr>
              <th>شناسه پیشنهاد</th>
              <th>نام محصول</th>
              <th>قیمت پیشنهادی فروش</th>
              <th>هزینه نهایی</th>
              <th>هزینه نهایی با احتساب ۲۰٪ بالاسری</th>
              <th>خالص سود و زیان</th>
              <th>درصد سود و زیان</th>
            </tr>
          </thead>
          <tbody>
            {data.products.map((product) => (
              <tr key={product.productId}>
                <td>{product.productId}</td>
                <td>{product.productTitle}</td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.purchasingPriceSuggestion)}
                  </span>
                  <span> میلیون تومان</span>
                </td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.finalCost)}
                  </span>
                  <span> میلیون تومان</span>
                </td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.finalCostMargin)}
                  </span>
                  <span> میلیون تومان</span>
                </td>
                <td>
                  <span className="ltr-value">
                    {formatNumber(product.profitLoss)}
                  </span>
                  <span> میلیون تومان</span>
                </td>
                <td>
                  <span className="ltr-value">
                    {Math.round(parseFloat(product.profitLossPercent))}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
